import { ButtonDock, miniButtonDockScrollBottomPad, translateTemplateLevelText, translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption, Text } from '@telegram-apps/telegram-ui';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page } from '../components/Page';
import { CommonIcon } from '../components/common/CommonIcon';
import {
    achievementUnlockFadeIn,
    achievementUnlockReveal,
    achievementUnlockRingExpand,
} from '../components/achievements/achievementUnlockAnimations';
import { getAchievementRarityColor } from '../helpers/achievement';
import { BRAND_PRIMARY } from '../helpers/brandColors';

export const CompletedAchievementsPage: FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);

    const { achievements = [] } = location.state || {};

    useEffect(() => {
        if (achievements.length === 0) navigate(-1);
    }, [achievements.length, navigate]);

    const currentAchievement = achievements[currentIndex];
    const hasNext = currentIndex < achievements.length - 1;
    const completedLevel = currentAchievement?.levelAchievedOn?.length || 1;
    const levelIndex = Math.max(completedLevel - 1, 0);
    const level = currentAchievement?.achievementLevels?.[levelIndex];
    const achievementName = level
        ? translateTemplateLevelText(t, currentAchievement.templateId, levelIndex, 'name', level.name)
        : translateTemplateText(t, currentAchievement.templateId, 'name', currentAchievement.name);
    const achievementDescription = level
        ? translateTemplateLevelText(t, currentAchievement.templateId, levelIndex, 'description', level.description)
        : translateTemplateText(t, currentAchievement.templateId, 'description', currentAchievement.description || '');

    const handleNext = useCallback(() => {
        if (hasNext) setCurrentIndex(previous => previous + 1);
    }, [hasNext]);

    const handleClose = useCallback(() => navigate(-1), [navigate]);

    if (!currentAchievement) return null;

    return (
        <Page
            back={false}
            insetEnabled={false}
            edgeToEdge
            footer={<ButtonDock main={{ text: t('actions.continue'), onClick: hasNext ? handleNext : handleClose }} />}
        >
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    overflow: 'hidden',
                    bgcolor: 'var(--tg-theme-secondary-bg-color)',
                    background: `radial-gradient(circle at 50% 35%, color-mix(in srgb, ${BRAND_PRIMARY} 24%, transparent), transparent 48%)`,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        paddingTop:
                            'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px) + 12px)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingBottom: miniButtonDockScrollBottomPad,
                    }}
                >
                    <Stack
                        key={`${currentAchievement.id}-${completedLevel}`}
                        alignItems="center"
                        justifyContent="center"
                        spacing={1.5}
                        sx={{ minHeight: '100%', px: 1, textAlign: 'center' }}
                    >
                        <Box sx={{ position: 'relative', width: 208, height: 256 }}>
                            {[0, 1].map(index => (
                                <Box
                                    key={index}
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: '36px 36px 104px 104px',
                                        border: `2px solid ${BRAND_PRIMARY}`,
                                        boxSizing: 'border-box',
                                        pointerEvents: 'none',
                                        animation: `${achievementUnlockRingExpand} 1.1s ease-out ${index * 0.3}s both`,
                                    }}
                                />
                            ))}
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '36px 36px 104px 104px',
                                    bgcolor: currentAchievement.iconImageColor || getAchievementRarityColor(currentAchievement.rarity),
                                    display: 'grid',
                                    placeItems: 'center',
                                    boxShadow: '0 14px 32px rgba(0,0,0,0.24)',
                                    animation: `${achievementUnlockFadeIn} 0.3s ease-out both`,
                                }}
                            >
                                {currentAchievement.iconImageUrl ? (
                                    <Box
                                        component="img"
                                        src={currentAchievement.iconImageUrl}
                                        alt={achievementName}
                                        sx={{ width: 92, height: 92, objectFit: 'contain' }}
                                    />
                                ) : (
                                    <CommonIcon icon={currentAchievement.icon || 'emoji_events'} type="outlined" color="#fff" size={92} />
                                )}
                            </Box>
                        </Box>
                        <Stack spacing={0.5} alignItems="center">
                            <Box sx={{ animation: `${achievementUnlockReveal} 0.4s ease-out 0.25s both` }}>
                                <Text weight="2" style={{ fontSize: 24 }}>
                                    {achievementName}
                                </Text>
                            </Box>
                            <Box sx={{ animation: `${achievementUnlockReveal} 0.4s ease-out 0.33s both` }}>
                                <Text style={{ color: 'var(--tg-theme-button-color)', fontSize: 13 }}>
                                    {t('achievement.level', { count: completedLevel })}
                                </Text>
                            </Box>
                            <Box sx={{ animation: `${achievementUnlockReveal} 0.4s ease-out 0.4s both` }}>
                                <Text style={{ color: 'var(--tg-theme-hint-color)', fontSize: 12 }}>{t('achievement.unlocked')}</Text>
                            </Box>
                            <Box sx={{ animation: `${achievementUnlockReveal} 0.4s ease-out 0.46s both` }}>
                                <Text style={{ maxWidth: 300, color: 'var(--tg-theme-hint-color)', fontSize: 14 }}>
                                    {achievementDescription || t('achievement.completedMessage')}
                                </Text>
                            </Box>
                        </Stack>
                        {achievements.length > 1 && (
                            <Stack direction="row" gap={1} alignItems="center">
                                {achievements.map((_: any, index: number) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor:
                                                index === currentIndex ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-hint-color)',
                                            opacity: index === currentIndex ? 1 : 0.3,
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}
                        <Caption data-testid="achievement-completion-caption" style={{ color: 'var(--tg-theme-hint-color)', fontSize: 12 }}>
                            {achievements.length > 1
                                ? t('achievement.achievementsCount', { current: currentIndex + 1, total: achievements.length })
                                : t('achievement.completedMessage')}
                        </Caption>
                    </Stack>
                </Box>
            </Box>
        </Page>
    );
};
